/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-05 11:29:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import { Icon } from '@shopify/polaris';
import { DeleteMinor } from '@shopify/polaris-icons';
import { change, getFormValues } from 'redux-form';

import GeneralForm from 'src/components/views/theme-settings/form';
import { MENU_ID } from 'src/constants/theme-settings.jsx';
import { handleResizeToolbar } from 'src/helpers/theme-settings-helper';
import * as ThemeSettingAction from 'src/stores/actions/theme-settings-actions';
import { DEVICE } from 'src/constants/theme-settings';

type Props = {
  upsertElementActions: (type: string, properties: Record<string, any>) => void
};

type ComposedProps = Props & WithI18nProps;

class RightPanel extends Component<ComposedProps, State> {
  state = {};

  componentWillReceiveProps(nextProps) {
    const { formValues } = this.props;
    const { formValues: nextFormValues, updateField } = nextProps;

    const toolbarSize = this.getToolbarSize(formValues);
    const nextToolbarSize = this.getToolbarSize(nextFormValues);
    const layout = _.get(formValues, 'toolbar_setting.layout');
    const nextLayout = _.get(nextFormValues, 'toolbar_setting.layout');

    if (toolbarSize !== nextToolbarSize && layout === nextLayout) {
      handleResizeToolbar(nextToolbarSize, updateField);
    }
  }

  getToolbarSize = (formValues): number => {
    if (_.isEmpty(formValues)) return 0;

    const elementKeys = _.keys(formValues).filter(
      key => /toolbar/.test(key) && key !== 'toolbar_setting'
    );

    const elementValues = _.pick(formValues, elementKeys);

    return _(elementValues).values().compact().size();
  };

  handleRemoveElement = () => {
    const {
      activeMenuItem,
      formValues,
      removeCurrentElement,
      updateField,
      themeName,
      upsertElementActions
    } = this.props;
    if (_.get(formValues, activeMenuItem, null)) {
      updateField(activeMenuItem, '');
    }

    const [prefix] = activeMenuItem.split('_');
    let groupOfKeys = Object.keys(formValues).filter(key => {
      const [, elementKey] = key.split('_');
      return (
        key.includes(prefix) &&
        !elementKey.includes('setting') &&
        !_.isEmpty(formValues[key]) &&
        key !== activeMenuItem
      );
    });
    groupOfKeys = _.orderBy(groupOfKeys, key => formValues[key].position, 'asc');
    groupOfKeys.forEach((key, index) => {
      updateField(`${key}.position`, index);
    });

    removeCurrentElement();
    const removeElement = this.props.activeMenuItemDetail?.value?.split('_');
    upsertElementActions('remove', {
      feature: 'Theme Settings',
      selectedTheme: themeName,
      page: removeElement[0],
      elementName: removeElement[0] + '_' + removeElement[1]
    });
    // mixpanelV3Helper.trackEventData('Remove visual element', {
    //   feature: 'Theme Settings',
    //   selectedTheme: themeName,
    //   page: removeElement[0],
    //   elementName: removeElement[1]
    // });
  };

  renderForm = () => {
    const {
      i18n,
      commonSetting,
      filterDataOptions,
      activeMenuItem,
      updateField,
      formValues,
      currentAppReviewIntegration,
      themeSettingDefaultValue,
      themeSettingDefaultThemeValue,
      labelTranslation,
      boostThemeLibName
    } = this.props;

    if (activeMenuItem) {
      if (activeMenuItem === MENU_ID.SEARCH_HEADER) {
        return (
          <p className="description-panel">
            {i18n.translate('AdminHTML.ThemeSetting.RightPanel.no_AvailableSetting')}
          </p>
        );
      }

      return (
        <div className="visual-editor-right-panel__form">
          <GeneralForm
            commonSetting={commonSetting}
            activeMenuItem={activeMenuItem}
            updateField={updateField}
            formValues={formValues}
            filterDataOptions={filterDataOptions}
            currentAppReviewIntegration={currentAppReviewIntegration}
            themeSettingDefaultValue={themeSettingDefaultValue?.value || {}}
            themeSettingDefaultThemeValue={themeSettingDefaultThemeValue?.value || {}}
            labelTranslation={labelTranslation}
            boostThemeLibName={boostThemeLibName}
          />
        </div>
      );
    }

    return (
      <p className="description-panel">
        {i18n.translate('AdminHTML.ThemeSetting.RightPanel.description')}
      </p>
    );
  };

  render() {
    const { i18n, activeMenuItemDetail, isExpandFullscreen } = this.props;

    let substituteTitle = activeMenuItemDetail?.title || activeMenuItemDetail?.label;
    substituteTitle = !substituteTitle
      ? i18n.translate('AdminHTML.ThemeSetting.RightPanel.title')
      : substituteTitle?.includes('AdminHTML')
      ? i18n.translate(substituteTitle)
      : substituteTitle;

    const contentClass = classnames('visual-editor-right-panel__content');
    const rightPanelContainerClass = classnames('visual-editor-right-panel-container', {
      opened: !isExpandFullscreen
    });
    const scrollClass = classnames('visual-editor-right-panel__scroll', {
      'not-remove': !activeMenuItemDetail?.value
    });
    return (
      <div className={rightPanelContainerClass}>
        <div className={scrollClass}>
          <div className="title-section">
            <span>{substituteTitle}</span>
            {/* <Tooltip content={i18n.translate('AdminHTML.ThemeSetting.Common.fullScreen')}>
              <span className="btn-collapse" onClick={this.handleToggleFullscreen}>
                <Icon source={ChevronRightMinor} color="base" />
              </span>
            </Tooltip> */}
          </div>

          <div className="visual-editor-right-panel">
            <div className={contentClass}>{this.renderForm()}</div>
          </div>
        </div>

        {!!activeMenuItemDetail?.value && (
          <div className="visual-editor-right-panel__remove" onClick={this.handleRemoveElement}>
            <Icon source={DeleteMinor} color="base" />
            <span>{i18n.translate('AdminHTML.ThemeSetting.Common.removeElement')}</span>
          </div>
        )}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateField: (field, value) => dispatch(change(`ThemeSettingVisualEditorForm`, field, value)),
  removeCurrentElement: () => dispatch(ThemeSettingAction.handleRemoveElement())
});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('ThemeSettingVisualEditorForm')(state) || {};
  const filterDataOptions = _.get(state.filterData.filterData, 'optionsList', []);
  const currentAppReviewIntegration = _.get(
    state.oauth.tenantApp,
    'currentAppReviewIntegration',
    ''
  );
  const isExpandFullscreen = _.get(state, 'themeSetting.device') === DEVICE.FULLSCREEN;

  return {
    formValues,
    filterDataOptions,
    currentAppReviewIntegration,
    isExpandFullscreen,
    activeMenuItem: _.get(state, 'themeSetting.activeMenuItem', ''),
    commonSetting: _.get(state, 'themeSetting.commonSetting', []),
    activeMenuItemDetail: _.get(state, 'themeSetting.activeMenuItemDetail', ''),
    themeSettingDefaultValue: _.get(state, 'themeSetting.themeSettingDefaultValue', {}),
    themeSettingDefaultThemeValue: _.get(state, 'themeSetting.themeSettingDefaultThemeValue', {}),
    labelTranslation: _.get(state, 'themeSetting.labelTranslation', {}),
    themeName: props.themeName,
    boostThemeLibName: _.get(state, 'themeSetting.boostThemeLibName', 'default')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(RightPanel));
